import React, { Fragment, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import MetaTags from "react-meta-tags";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import { AppInstance } from "../../api/instance";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useSettings } from "../../hooks/useSettings";
import { useAuthContext } from "../../contexts/AuthContext";

const MyOrders = ({ location, userInfo }) => {
  const navigate = useHistory();
  const { pathname } = location;
  const { siteName } = useSettings();
  const { openAuthModal } = useAuthContext();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 25,
  });

  const getPrimaryImageUrl = (item) => {
    if (!item) return "";
    if (item.images && item.images.length > 0) {
      const sorted = [...item.images].sort(
        (a, b) => (a.position || 0) - (b.position || 0)
      );
      return sorted[0]?.url || "";
    }
    if (item.product && item.product.cover) {
      return (
        process.env.REACT_APP_IMAGE_BASE_URL + item.product.cover
      );
    }
    return "";
  };

  const getOrders = async (page = 1) => {
    if (!userInfo?.token) {
      openAuthModal("login");
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await AppInstance.get("/order-items", {
        params: {
          page: page,
          per_page: 25,
        },
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setOrders(data.data);
      setPagination(data.pagination);
    } catch (error) {
      setIsLoading(false);
      if (error?.response?.status === 401) {
        openAuthModal("login");
      }
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getOrders();
  }, []);

  const handleRowClick = (orderId) => {
    navigate.push(`/order-detail/${orderId}`);
  };

  const handleProductClick = (e, variantId) => {
    e.stopPropagation();
    navigate.push(`/product/${variantId}`);
  };

  const handlePageChange = (page) => {
    getOrders(page);
  };

  // Get the most recent status (first in the array is the latest)
  const getLatestStatus = (statuses) => {
    if (!statuses || statuses.length === 0)
      return { status: "UNKNOWN", description: "Status unknown" };
    return statuses[0];
  };

  const formatStatusText = (status) => String(status || "").replace(/_/g, " ");

  return (
    <Fragment>
      <MetaTags>
        <title>{siteName} | My Orders</title>
        <meta name="description" content={`My Orders page of ${siteName}`} />
      </MetaTags>

      <BreadcrumbsItem to={process.env.PUBLIC_URL + "/"}>Home</BreadcrumbsItem>
      <BreadcrumbsItem to={process.env.PUBLIC_URL + pathname}>
        My Orders
      </BreadcrumbsItem>

      <LayoutOne headerTop="visible" headerContainerClass>
        <Breadcrumb />
        <div className="cart-main-area pb-100">
          <div className="container-fluid p-md-5 my-orders-wrapper">
            {isLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden"></span>
                </div>
              </div>
            ) : orders.length > 0 ? (
              <Fragment>
                <h3 className="cart-page-title mb-4">Your Orders</h3>
                <div className="row">
                  {console.log(orders)}
                  {orders.map((order, index) => {
                    const latestStatus = getLatestStatus(order.statuses);
                    return (
                      <div
                        key={`${order?.id}-${index}`}
                        className="col-12 mb-4"
                      >
                        <div
                          className="order-card"
                          onClick={() => handleRowClick(order.id)}
                        >
                          <div className="order-card__header">
                            <div className="order-number">
                             Order #{order.code}
                            </div>
                            <div className="order-status">
                              <span
                                className={`status-badge status-${latestStatus.status.toLowerCase()}`}
                              >
                                {formatStatusText(latestStatus.status)}
                              </span>
                            </div>
                          </div>

                          <div className="order-card__body">
                            <div className="product-section">
                              <div className="product-image">
                                <div
                                  onClick={(e) =>
                                    handleProductClick(
                                      e,
                                      order?.item?.id
                                    )
                                  }
                                >
                                  <img
                                    className="img-fluid"
                                    src={getPrimaryImageUrl(order.item)}
                                    alt={order.item.product.name}
                                  />
                                </div>
                              </div>

                              <div className="product-details">
                                <h6 className="product-name">
                                  {order.item.product.name}
                                </h6>
                                <p className="product-variant">
                                  {order.item.name}
                                </p>
                                {order.item.attributes &&
                                  order.item.attributes.length > 0 && (
                                    <div className="product-attributes">
                                      {order.item.attributes.map(
                                        (attr, attrIndex) => (
                                          <span
                                            key={attrIndex}
                                            className="attribute-tag"
                                          >
                                            {attr.attribute.name}: {attr.value}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="pagination-wrapper mt-4">
                    <nav aria-label="Orders pagination">
                      <ul className="pagination justify-content-center">
                        <li
                          className={`page-item ${
                            pagination.current_page === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              handlePageChange(pagination.current_page - 1)
                            }
                            disabled={pagination.current_page === 1}
                          >
                            Previous
                          </button>
                        </li>

                        {Array.from(
                          { length: pagination.last_page },
                          (_, i) => i + 1
                        ).map((page) => (
                          <li
                            key={page}
                            className={`page-item ${
                              page === pagination.current_page ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}

                        <li
                          className={`page-item ${
                            pagination.current_page === pagination.last_page
                              ? "disabled"
                              : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              handlePageChange(pagination.current_page + 1)
                            }
                            disabled={
                              pagination.current_page === pagination.last_page
                            }
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </Fragment>
            ) : (
              <div className="item-empty-area text-center mt-5">
                <div className="item-empty-area__icon mb-30">
                  <i className="pe-7s-cart" style={{ fontSize: "80px" }}></i>
                </div>
                <div className="item-empty-area__text">
                  No orders found <br />
                  <Link
                    to={process.env.PUBLIC_URL + "/products"}
                    className="btn btn-primary mt-3"
                  >
                    Shop Now
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </LayoutOne>
    </Fragment>
  );
};

MyOrders.propTypes = {
  userInfo: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    userInfo: state.userInfo,
  };
};

export default connect(mapStateToProps)(MyOrders);
